import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { LanguageToggle } from "@/components/Header";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function AuthShell({ title, subtitle, children }) {
  const { t } = useLang();
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between bg-primary p-12 text-primary-foreground">
        <Link to="/" className="flex items-center gap-2">
          <span className="h-9 w-9 rounded-xl bg-primary-foreground/15 flex items-center justify-center">
            <Sparkles className="h-5 w-5" />
          </span>
          <span className="font-heading font-extrabold text-lg">Brilla</span>
        </Link>
        <div className="space-y-4">
          <h2 className="font-heading font-extrabold text-4xl tracking-tight leading-tight">{t("landing.heroTitle")}</h2>
          <p className="text-primary-foreground/80 text-lg">{t("tagline")}</p>
        </div>
        <p className="text-primary-foreground/70 text-sm">{t("landing.developedBy")}</p>
      </div>
      <div className="flex flex-col p-6 sm:p-12">
        <div className="flex justify-end"><LanguageToggle /></div>
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-sm space-y-6">
            <div className="space-y-1">
              <h1 className="font-heading font-extrabold text-3xl tracking-tight">{title}</h1>
              <p className="text-muted-foreground">{subtitle}</p>
            </div>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { t } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.role === "admin") navigate("/admin");
      else if (user.role === "coach") navigate("/coach");
      else navigate(user.onboarding_completed ? "/dashboard" : "/onboarding");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={t("auth.loginTitle")} subtitle={t("auth.loginSubtitle")}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email">{t("auth.email")}</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="h-12 rounded-xl" data-testid="login-email" placeholder="teacher@brilla.com" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">{t("auth.password")}</Label>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            className="h-12 rounded-xl" data-testid="login-password" />
        </div>
        <div className="text-right">
          <Link to="/forgot-password" className="text-sm text-accent font-semibold hover:underline" data-testid="forgot-link">{t("auth.forgot")}</Link>
        </div>
        <Button type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-secondary hover:bg-secondary/90 text-base" data-testid="login-submit">
          {loading ? t("common.loading") : t("auth.loginBtn")}
        </Button>
        <p className="text-center text-sm text-muted-foreground">
          {t("auth.noAccount")} <Link to="/register" className="text-accent font-semibold hover:underline" data-testid="to-register">{t("auth.registerBtn")}</Link>
        </p>
      </form>
    </AuthShell>
  );
}
